import common from './common';
import SplashScene from './scenes/SplashScene';
import IntroScene from './scenes/IntroScene';
import GameScene from './scenes/GameScene';
import LevelSelectScene from './scenes/LevelSelectScene';
import EndScene from './scenes/EndScene';
import TutorialScene from './scenes/TutorialScene';
import PauseScene from './scenes/PauseScene';
import CompetitionOverlay from './scenes/CompetitionOverlay';
import OutroScene from './scenes/OutroScene';
import FakeLoadingScene from './scenes/FakeLoadingScene';

export default class Application {
	init() {
		common.cheatActive = common.config.cheat_mode;

		if (!(/MSIE|Trident/).test(navigator.userAgent)) {			// Skip IE11 because URL is not compatible.
			const urlStr = window.location.href;
			const url = new URL(urlStr);
			const option = url.searchParams.get('level');

			if (option) {
				common.currentLevel = Number(option);
				common.levelSettings = P3.assets.json('level_data')[common.currentLevel];

				this.showGameScene(option);
			} else {
				this.showSplashScene();
			}
		} else {
			this.showSplashScene();
		}
	}

	showSplashScene() {
		const scene = new SplashScene();

		scene.autoAppear = false;

		scene.signals.next.add(() => {
			if (common.storage.data.intro) {
				this.showIntroScene();
			} else {
				P3.audio.stopMusic();
				this.showLevelSelectScene();
			}
		}, this);

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showIntroScene() {
		const scene = new IntroScene();

		scene.autoAppear = false;

		scene.signals.next.add(() => {
			common.storage.data.intro = false;
			common.storage.save();

			this.showLevelSelectScene();
		}, this);

		scene.signals.previous.add(() => {
			this.showSplashScene();
		}, this);

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showLevelSelectScene() {
		const scene = new LevelSelectScene();

		scene.autoAppear = false;

		scene.signals.next.add((level) => {
			this.showGameScene(level);
		}, this);
		scene.signals.previous.add(() => {
			this.showSplashScene();
		}, this);
		scene.signals.pause.add(() => {
			scene.onPause();
			this.showCompetitionScene(scene);
		});

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	/**
     * @param {Number} steps The steps the user took inside the level
     * @param {String} level The current level, used for the replay button
	 */
	showEndScene(score, level) {
		const scene = new EndScene(score);

		scene.autoAppear = false;

		scene.signals.next.add(() => {
			common.currentLevel++;

			if (P3.assets.json('level_data')[common.currentLevel].debug_only) {
				// prevents crash on level select screen because of #levels
				common.currentLevel--;

				this.showOutroScene();
			} else {
				this.showGameScene(common.currentLevel);
			}
		}, this);
		scene.signals.previous.add(() => {
			this.showGameScene(level);
		}, this);
		scene.signals.home.add(() => {
			this.showSplashScene();
		}, this);
		scene.signals.pause.add(() => {
			scene.onPause();
			this.showCompetitionScene(scene);
		});

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showGameScene(level) {
		const scene = new GameScene(level);

		scene.autoAppear = false;

		scene.signals.next.add((score) => {
			this.showEndScene(score, level);
		}, this);
		scene.signals.previous.add(() => {
			this.showLevelSelectScene();
		}, this);
		scene.signals.fakeload.add(() => {
			this.showLoadingScene();
		}, this);
		scene.signals.pause.add(() => {
			this.showPauseScene(scene);
		}, this);

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showOutroScene() {
		const scene = new OutroScene();

		scene.autoAppear = false;

		scene.signals.next.add(() => {
			this.showLevelSelectScene();
		}, this);

		this.fadeTransition(() => {
			P3.scene.show(scene);
		}, () => {
			scene.appear();
		}, this);

		return scene;
	}

	showPauseScene(previousScene) {
		const scene = new PauseScene();

		scene.autoAppear = false;

		scene.signals.next.add((dest) => {
			switch (dest) {
				case 'home': this.showSplashScene(); break;
				case 'level': this.showLevelSelectScene(); break;
				case 'tutorial': this.showTutorialScene(); break;
			}
		});
		scene.signals.previous.add(() => {
			if (previousScene.onResume) {
				previousScene.onResume();
			}

			P3.scene.remove();
		}, this);

		P3.scene.add(scene);

		return scene;
	}

	showLoadingScene() {
		const scene = new FakeLoadingScene();

		scene.autoAppear = false;

		scene.signals.next.add(() => {
			this.fadeTransition(() => {
				P3.scene.remove();
			}, null, this);
		});

		P3.scene.add(scene);

		return scene;
	}

	showCompetitionScene(previousScene) {
		const scene = new CompetitionOverlay();

		scene.autoAppear = false;

		scene.signals.previous.add((result) => {
			P3.scene.remove();
			previousScene.onResume(result);
		}, this);

		P3.scene.add(scene);

		return scene;
	}

	showTutorialScene() {
		const scene = new TutorialScene();

		scene.autoAppear = false;

		scene.signals.previous.add(() => {
			P3.scene.remove();
		}, this);

		P3.scene.add(scene);

		return scene;
	}

	fadeTransition(a, b, scope) {
		scope = scope || window;

		const transition = new P3.FadeTransition(0x0);

		common.transitions.addChild(transition);

		transition.signals.in.add(() => {
			transition.out();
			a && a.call(scope);
		}, this);

		transition.signals.out.add(() => {
			transition.destroy();
			b && b.call(scope);
		}, this);
		transition.in();
	}
}
